import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import LockManager from "../../utils/lockManager";
import { BUSY_TYPES } from "../../utils/busy";
import { compensationProceduresService } from "../../services/compensation-procedures-service";
import { ResponseError } from "types/common/responses/responses-types";
import { Column, FetchTableDataParams } from "types/common/table/table-types";
import {
    CompensationProcedure,
    CompensationProcedureDetailed,
    CompensationProcedureState,
    CompensateRisksData,
} from "@/types/compensation-procedures";
import { RootState } from "store";

const lockManager = new LockManager("compensationProcedure");

const initialState: CompensationProcedureState["admin"]["compensationProcedures"] = {
    compensationProcedures: [],
    columns: [],
    total: 0,
    selectedIds: [],
    searchString: "",
    pageNumber: 1,
    limitNumber: 10,
    sortData: [],
    filterData: [],
    busy: false,
    busyType: null,
    error: null,
    submit: false,
    currentCompensationProcedure: null,
    currentCompensationProcedureEditable: false,
    currentCompensationProcedureDeleted: true,
    validationErrors: null,
    compensateRisksData: null,
};

export const fetchCompensationProcedures = createAsyncThunk<
    { compensationProcedures: CompensationProcedure[]; total: number },
    { params: FetchTableDataParams },
    { rejectValue: ResponseError }
>("admin/fetchCompensationProcedures", async ({ params }, { rejectWithValue }) => {
    try {
        const response = await compensationProceduresService.getCompensationProcedures(params);
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const fetchCompensationProcedureColumns = createAsyncThunk<Column[], void, { rejectValue: ResponseError }>(
    "admin/fetchCompensationProcedureColumns",
    async (_, { rejectWithValue }) => {
        try {
            const response = await compensationProceduresService.getCompensationProceduresColumns();
            return response;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const fetchCompensationProcedureDetailed = createAsyncThunk<
    { compensationProcedure: CompensationProcedureDetailed; lock },
    string,
    { rejectValue: ResponseError }
>("admin/fetchCompensationProcedureDetailed", async (id, { rejectWithValue }) => {
    try {
        const compensationProcedure = await compensationProceduresService.getCompensationProcedureDetailed(id);
        const lock = await lockManager.lockObject(id);
        return { compensationProcedure, lock };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const createCompensationProcedure = createAsyncThunk<
    CompensationProcedureDetailed,
    CompensationProcedureDetailed,
    { rejectValue: ResponseError }
>("admin/createCompensationProcedure", async (data, { rejectWithValue }) => {
    try {
        const response = await compensationProceduresService.createCompensationProcedure(data);
        return response;
    } catch (err) {
        return rejectWithValue(err as ResponseError);
    }
});

export const editCompensationProcedure = createAsyncThunk<
    CompensationProcedureDetailed,
    CompensationProcedureDetailed,
    { rejectValue: ResponseError }
>("admin/editCompensationProcedure", async (data, { rejectWithValue }) => {
    try {
        const response = await compensationProceduresService.editCompensationProcedure(data);
        return response;
    } catch (err) {
        return rejectWithValue(err as ResponseError);
    }
});

export const deleteCompensationProcedure = createAsyncThunk<string, string, { rejectValue: ResponseError }>(
    "admin/deleteCompensationProcedure",
    async (id, { rejectWithValue }) => {
        try {
            await compensationProceduresService.deleteCompensationProcedure(id);
            return id;
        } catch (err) {
            return rejectWithValue(err as ResponseError);
        }
    }
);

export const clearCurrentCompensationProcedure = createAsyncThunk<void, void, { state: RootState }>(
    "admin/clearCurrentCompensationProcedure",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        const current = state.admin.compensationProcedures.currentCompensationProcedure;
        const editable = state.admin.compensationProcedures.currentCompensationProcedureEditable;
        const deleted = state.admin.compensationProcedures.currentCompensationProcedureDeleted;

        if (!current) return rejectWithValue(["Current compensationProcedure is not initialized"]);
        if (deleted || !editable || !current.id) return;

        const res = await lockManager.unlockObject(current.id);
        if (!res.result) return rejectWithValue(res.messages);
    }
);

export const fetchCompensateRisksData = createAsyncThunk<CompensateRisksData, void, { rejectValue: ResponseError; state: RootState }>(
    "admin/fetchCompensateRisksData",
    async (_, { rejectWithValue, getState }) => {
        const state = getState();
        // @ts-expect-error: User level report is not typed
        const users = state.reports.userLevel.bufferedParameters?.usernames;
        // @ts-expect-error: User level report is not typed
        const risks = state.reports.userLevel.bufferedParameters?.risks;

        try {
            const response = await compensationProceduresService.getDataForCompensateRisks(users, risks);
            return response;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const createCompensation = createAsyncThunk<void, CompensateRisksData, { rejectValue: ResponseError; state: RootState }>(
    "admin/createCompensation",
    async (form, { rejectWithValue, getState }) => {
        const state = getState();

        const users = state.reports.userLevel.users;
        const risks = state.reports.userLevel.risks;

        try {
            await compensationProceduresService.createCompensation(form, users, risks);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

const compensationProceduresSlice = createSlice({
    name: "compensationProcedures",
    initialState,
    reducers: {
        setSubmit(state, action) {
            state.submit = action.payload;
        },
        setBusy(state, action) {
            state.busy = action.payload;
        },
        setInitialCurrentCompensationProcedure(state) {
            state.currentCompensationProcedure = { id: "", description: "", risks: [], compensatedEmployees: [] };
            state.error = null;
            state.validationErrors = null;
            state.submit = false;
            state.currentCompensationProcedureEditable = true;
            state.currentCompensationProcedureDeleted = false;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        setValidationErrors(state, action) {
            state.validationErrors = action.payload;
        },
        setSelectedIds(state, action) {
            state.selectedIds = action.payload;
        },
        setSearchString(state, action) {
            if (state.searchString !== action.payload) {
                state.pageNumber = 1;
                state.searchString = action.payload;
            }
        },
        setPageNumber(state, action) {
            state.pageNumber = action.payload;
        },
        setLimitNumber(state, action) {
            state.pageNumber = 1;
            state.limitNumber = action.payload;
        },
        setSortData(state, action) {
            state.pageNumber = 1;
            state.sortData = action.payload;
        },
        setFilterData(state, action) {
            state.pageNumber = 1;
            state.filterData = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder

            // fetch all
            .addCase(fetchCompensationProcedures.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchCompensationProcedures.fulfilled, (state, action) => {
                state.busy = false;
                state.compensationProcedures = action.payload.compensationProcedures;
                state.total = action.payload.total;
                state.error = null;
                state.validationErrors = null;
            })
            .addCase(fetchCompensationProcedures.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })

            // fetch detailed
            .addCase(fetchCompensationProcedureDetailed.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.currentCompensationProcedureDeleted = false;
                state.submit = false;
            })
            .addCase(fetchCompensationProcedureDetailed.fulfilled, (state, action) => {
                const { compensationProcedure, lock } = action.payload;
                state.currentCompensationProcedure = compensationProcedure;
                state.busy = false;
                state.currentCompensationProcedureEditable = lock.result;
                state.error = lock.result ? null : lock.messages;
            })
            .addCase(fetchCompensationProcedureDetailed.rejected, (state, action) => {
                state.currentCompensationProcedure = null;
                state.error = action.payload || null;
                state.busy = false;
                state.currentCompensationProcedureEditable = false;
            })

            // clear
            .addCase(clearCurrentCompensationProcedure.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(clearCurrentCompensationProcedure.fulfilled, (state) => {
                state.busy = false;
                state.currentCompensationProcedure = null;
                state.currentCompensationProcedureEditable = false;
                state.currentCompensationProcedureDeleted = false;
                state.error = null;
                state.validationErrors = null;
                state.submit = false;
            })
            .addCase(clearCurrentCompensationProcedure.rejected, (state) => {
                state.busy = false;
                // state.error = action.payload || null;
            })

            // create
            .addCase(createCompensationProcedure.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
                state.submit = false;
            })
            .addCase(createCompensationProcedure.fulfilled, (state, action) => {
                state.currentCompensationProcedure = action.payload;
                state.busy = false;
                state.submit = true;
                state.error = null;
                state.validationErrors = null;
            })
            .addCase(createCompensationProcedure.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                if (error.code === "validation") {
                    state.validationErrors = error.errors;
                }

                state.error = error;
            })

            // edit
            .addCase(editCompensationProcedure.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
                state.submit = false;
            })
            .addCase(editCompensationProcedure.fulfilled, (state, action) => {
                state.busy = false;
                state.submit = true;
                state.error = null;
                state.validationErrors = null;
                state.currentCompensationProcedure = action.payload;
            })
            .addCase(editCompensationProcedure.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                if (error.code === "validation") {
                    state.validationErrors = error.errors;
                }

                state.error = error;
            })

            // delete
            .addCase(deleteCompensationProcedure.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })
            .addCase(deleteCompensationProcedure.fulfilled, (state, action) => {
                const id = action.payload;
                state.compensationProcedures = state.compensationProcedures.filter((item) => item.id !== id);
                if (state.currentCompensationProcedure?.id === id) {
                    state.submit = true;
                    state.currentCompensationProcedureDeleted = true;
                }
                state.busy = false;
                state.error = null;
                state.validationErrors = null;
            })
            .addCase(deleteCompensationProcedure.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })

            // columns
            .addCase(fetchCompensationProcedureColumns.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchCompensationProcedureColumns.fulfilled, (state, action) => {
                state.busy = false;
                state.columns = action.payload;
            })
            .addCase(fetchCompensationProcedureColumns.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(fetchCompensateRisksData.fulfilled, (state, action) => {
                state.busy = false;
                state.compensateRisksData = action.payload;
            })
            .addCase(fetchCompensateRisksData.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(fetchCompensateRisksData.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(createCompensation.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(createCompensation.fulfilled, (state) => {
                state.busy = false;
                //state.compensateRisksData = action.payload;
            })
            .addCase(createCompensation.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                if (error.code === "validation") {
                    state.validationErrors = error.errors;
                }

                state.error = error;
            });
    },
});

export const {
    setSubmit,
    setBusy,
    setInitialCurrentCompensationProcedure,
    setError,
    setValidationErrors,
    setSelectedIds,
    setSearchString,
    setPageNumber,
    setLimitNumber,
    setSortData,
    setFilterData,
} = compensationProceduresSlice.actions;

export default compensationProceduresSlice.reducer;
